import React, { useState, useEffect, useRef } from 'react';
import { ImageOff, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ImageWithAuth — Reusable component for rendering images from authenticated endpoints.
 *
 * Fetches the image blob with a Bearer token, creates a local Object URL, and renders it.
 * Handles loading, error states, and cleans up Object URLs on unmount to prevent memory leaks.
 *
 * Usage:
 *   <ImageWithAuth fileId="abc-123" alt="Moodboard" className="w-full rounded-xl" />
 *   <ImageWithAuth src="/api/storage/files/abc-123" alt="Receipt" />
 *   <ImageWithAuth src="https://example.com/public.jpg" alt="External" />   // passthrough for public URLs
 *
 * Props:
 *   fileId      — Storage file ID (constructs URL automatically)
 *   src         — Full URL (alternative to fileId). Public URLs are rendered directly.
 *   alt         — Alt text
 *   className   — CSS classes for the <img>
 *   style       — Inline styles for the <img>
 *   fallback    — Custom fallback component on error
 *   onLoad      — Callback when image loads successfully
 *   onError     — Callback when image fails
 */
export const ImageWithAuth = ({
  fileId,
  src,
  alt = '',
  className = '',
  style,
  fallback,
  onLoad,
  onError,
  ...rest
}) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const objectUrlRef = useRef(null);
  const abortRef = useRef(null);

  // Determine the actual URL to fetch
  const resolvedUrl = fileId
    ? `${API}/api/storage/files/${fileId}`
    : src;

  // Check if it's a protected endpoint (needs auth) vs a public URL
  const isProtected = resolvedUrl?.includes('/api/storage/files/');

  useEffect(() => {
    if (!resolvedUrl) {
      setLoading(false);
      setError(true);
      return;
    }

    // Public images: just set the url directly, let browser handle
    if (!isProtected) {
      setBlobUrl(resolvedUrl);
      setLoading(false);
      return;
    }

    // Protected: fetch with auth token
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(false);

    const fetchImage = async () => {
      try {
        const resp = await fetch(resolvedUrl, {
          credentials: 'include',
          signal: controller.signal,
        });

        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

        const blob = await resp.blob();
        if (controller.signal.aborted) return;

        // Cleanup previous object URL before creating new one
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
        }

        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setBlobUrl(url);
        setLoading(false);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('ImageWithAuth fetch failed:', err.message);
        setError(true);
        setLoading(false);
        onError?.();
      }
    };

    fetchImage();

    return () => {
      controller.abort();
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [resolvedUrl, isProtected]);

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center bg-stone-100 ${className}`}
        style={{ minHeight: 80, ...style }}
        data-testid="image-auth-loading"
      >
        <Loader2 className="w-5 h-5 text-stone-300 animate-spin" />
      </div>
    );
  }

  if (error) {
    if (fallback) return fallback;
    return (
      <div
        className={`flex flex-col items-center justify-center bg-stone-50 text-stone-300 ${className}`}
        style={{ minHeight: 80, ...style }}
        data-testid="image-auth-error"
      >
        <ImageOff className="w-5 h-5 mb-1" />
        <span className="text-[9px] font-sans">Could not laste bilde</span>
      </div>
    );
  }

  return (
    <img
      src={blobUrl}
      alt={alt}
      className={className}
      style={style}
      data-testid="image-auth-loaded"
      onLoad={onLoad}
      onError={() => {
        setError(true);
        onError?.();
      }}
      {...rest}
    />
  );
};
